import { useForm } from "@inertiajs/react";
import { FileText } from "lucide-react";
import { FormError } from "@/components/admin/CampaignPlanFields";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";
import { Button } from "@/components/ui/button";
import { formatDate, formatMoney } from "@/lib/format";

/**
 * Receipts sent for a campaign's fee, and the step after recording a
 * payment on the admin school page: email a school admin the PDF receipt.
 * The form shows while some of the plan price has no receipt yet (after
 * approval, or the difference after an upgrade); the server refuses
 * receipts beyond the plan price.
 */
export default function CampaignReceipts({ campaign, recipients, defaultRecipientId, paymentMethods, today }) {
  const outstanding = campaign.receipt_outstanding_cents;

  return (
    <div className="border-t border-border pt-4 space-y-3">
      <div className="text-sm font-medium">Receipts</div>
      {campaign.receipts.length > 0 && (
        <ul className="space-y-1 text-sm">
          {campaign.receipts.map((receipt) => (
            <li key={receipt.id} className="flex flex-wrap items-center gap-x-2">
              <a href={receipt.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <FileText className="h-3.5 w-3.5" />
                No. {receipt.number}
              </a>
              <span className="text-muted-foreground">
                {formatMoney(receipt.amount_cents, { cents: true })} · paid {formatDate(receipt.paid_on)} · sent to {receipt.sent_to}
              </span>
            </li>
          ))}
        </ul>
      )}
      {outstanding > 0 && (
        <ReceiptForm
          // Remount when the plan changes so the amount follows it.
          key={outstanding}
          campaign={campaign}
          recipients={recipients}
          defaultRecipientId={defaultRecipientId}
          paymentMethods={paymentMethods}
          today={today}
        />
      )}
    </div>
  );
}

function ReceiptForm({ campaign, recipients, defaultRecipientId, paymentMethods, today }) {
  const outstanding = campaign.receipt_outstanding_cents;
  const idPrefix = `receipt-${campaign.id}`;
  const form = useForm({
    // The admin who signed the school up, else none: the platform admin
    // picks.
    recipient_id: defaultRecipientId ? String(defaultRecipientId) : "",
    amount_dollars: String(outstanding / 100),
    paid_on: today,
    payment_method: paymentMethods[0],
    payment_reference: "",
  });

  if (recipients.length === 0) {
    return <p className="text-sm text-muted-foreground">The school has no admins to send a receipt to.</p>;
  }

  const send = (e) => {
    e.preventDefault();
    form.post(campaign.receipts_path, {
      preserveScroll: true,
      preserveState: "errors",
    });
  };

  return (
    <form onSubmit={send} className="space-y-4 rounded-lg bg-muted/40 p-4">
      <p className="text-sm text-muted-foreground">
        Send a PDF receipt for the {formatMoney(outstanding, { cents: true })} payment so the school can give it to its bookkeeper.
      </p>
      <SelectField
        id={`${idPrefix}-recipient`}
        label="Send to"
        value={form.data.recipient_id}
        onChange={(e) => form.setData("recipient_id", e.target.value)}
        options={recipients.map((r) => ({ value: String(r.id), label: r.label }))}
        placeholder="Choose a school admin"
        required
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          id={`${idPrefix}-amount`}
          label="Amount paid (CAD)"
          type="number"
          min="0.01"
          step="0.01"
          value={form.data.amount_dollars}
          onChange={(e) => form.setData("amount_dollars", e.target.value)}
        />
        <FormField
          id={`${idPrefix}-paid-on`}
          label="Paid on"
          type="date"
          max={today}
          value={form.data.paid_on}
          onChange={(e) => form.setData("paid_on", e.target.value)}
        />
        <SelectField
          id={`${idPrefix}-method`}
          label="Payment method"
          value={form.data.payment_method}
          onChange={(e) => form.setData("payment_method", e.target.value)}
          options={paymentMethods}
        />
        <FormField
          id={`${idPrefix}-reference`}
          label="Reference (optional)"
          placeholder="e-Transfer reference, cheque number…"
          value={form.data.payment_reference}
          onChange={(e) => form.setData("payment_reference", e.target.value)}
        />
      </div>
      <FormError message={form.errors.base} />
      <Button type="submit" size="sm" disabled={form.processing}>
        {form.processing ? "Sending…" : "Send receipt"}
      </Button>
    </form>
  );
}
